import { createFileRoute, notFound } from "@tanstack/react-router";

import {
  CONTENT_CACHE_CONTROL,
  CONTENT_NOT_FOUND_CACHE_CONTROL,
} from "@/content/cachePolicy";
import { priceListCms } from "@/content/priceListSource";
import { locationPageCms } from "@/content/locationSource";
import { jsonLdVerbatim } from "@/content/schema";
import LocationPage from "./-components/LocationPage";
import { NotFoundPage } from "@/components/landing/LandingNotFound";
import { COUNTRY_PRICE_LIST } from "./-components/locationPricing";
import { locationSchema } from "./-components/locationSchema";

/**
 * Every `/proxy-locations/:slug` page, from one route over the CMS's countries.
 *
 * The country and the words of its page are its `proxyLocation`, the lists the
 * whole order of them (`locationSource.ts`); the page list the build
 * prerenders from, and the server after a publish, is the same set
 * (`src/content/prerenderPages.ts`). The price table's rows are the CMS's
 * Proxy Server list, read once for all of them. A build without that list
 * stops rather than ship a country page with no prices.
 *
 * A slug the CMS does not hold throws `notFound()` rather than rendering an
 * empty template, so a retired country answers 404 the way the published site
 * does instead of a blank 200.
 */
export const Route = createFileRoute("/proxy-locations/$slug")({
  loader: async ({ params }) => {
    const page = await locationPageCms({ data: params.slug });
    if (!page) {
      throw notFound();
    }
    const prices = await priceListCms({ data: COUNTRY_PRICE_LIST });
    if (!prices) {
      throw new Error(`The CMS holds no ${COUNTRY_PRICE_LIST}.`);
    }
    return { ...page, prices };
  },
  headers: ({ loaderData }) => ({
    "Cache-Control": loaderData
      ? CONTENT_CACHE_CONTROL
      : CONTENT_NOT_FOUND_CACHE_CONTROL,
  }),
  // `head` runs before the loader settles on a client-side navigation, so the
  // absence of loaderData is expected rather than a fault. A `script` is not
  // hoisted by React, which is why the structured data is emitted here and the
  // rest of the head is JSX inside the page.
  head: ({ loaderData }) =>
    loaderData
      ? {
          scripts: [
            {
              type: "application/ld+json",
              children: jsonLdVerbatim(locationSchema(loaderData.country)),
            },
          ],
        }
      : {},
  component: ProxyLocationRoute,
  notFoundComponent: NotFoundPage,
});

function ProxyLocationRoute() {
  const { country, locations, prices } = Route.useLoaderData();
  return (
    <LocationPage country={country} locations={locations} prices={prices} />
  );
}
